export interface TimelineOptions {
  start: Date;
  end: Date;
}

export interface TimelineBlock {
  offset: number;
  duration: number;
}

export interface Timeline {
  start: Date;
  end: Date;
  hours: string[];
  selection: TimelineBlock;
  getBlock(options: TimelineBlockOptions): TimelineBlock;
}

export interface TimelineBlockOptions {
  start: Date;
  end: Date;
  minDuration?: number;
}

const TimelineDefaultStartHour = 8;
const TimelineDurationHours = 24;
const TimelinePaddingHours = 2;

const BlockSteps = 4;
const BlockMinutes = 60 / BlockSteps;
const BlockMinutesInMilliseconds = BlockMinutes * 60 * 1000;

/**
 * generates the timeline `start` and `end` time and `hours` with 2 hours padding at start and end,
 * generates `selection` block of time,
 * returns `getBlock` method to generate more blocks
 */
export function createTimeline({start, end}: TimelineOptions): Timeline {
  const timelineStart = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
    getTimelineStartHour(start, end)
  );
  const timelineEnd = new Date(timelineStart.valueOf() + TimelineDurationHours * 60 * 60 * 1000);

  return {
    start: timelineStart,
    end: timelineEnd,
    hours: createHoursRange(timelineStart.getHours(), TimelineDurationHours),
    selection: getBlock({start, end, minDuration: 0.5}),
    getBlock,
  };

  /**
   * generates block of time from start to end within timeline
   */
  function getBlock({start: blockStart, end: blockEnd, minDuration = 0}: TimelineBlockOptions): TimelineBlock {
    const timelineStartAndBlockStartDiff = getDurationBetweenInHoursAndMinutes(
      timelineStart,
      floorDate(blockStart, BlockMinutesInMilliseconds)
    );

    const blockStartAndTimelineDuration = roundHoursAndMinutesToBlockSteps(
      timelineStartAndBlockStartDiff.hours,
      timelineStartAndBlockStartDiff.minutes
    );

    const blockOffset = Math.max(blockStartAndTimelineDuration, 0);

    if (blockOffset >= TimelineDurationHours) {
      return null;
    }

    const blockDurationDiff = getDurationBetweenInHoursAndMinutes(
      floorDate(blockStart, BlockMinutesInMilliseconds),
      ceilDate(blockEnd, BlockMinutesInMilliseconds)
    );

    const offsetNegativeDuration = Math.min(blockStartAndTimelineDuration, 0);
    const blockDuration = Math.min(
      Math.max(
        roundHoursAndMinutesToBlockSteps(blockDurationDiff.hours, blockDurationDiff.minutes) + offsetNegativeDuration,
        minDuration
      ),
      TimelineDurationHours - blockOffset
    );

    if (blockDuration <= 0) {
      return null;
    }

    return {offset: blockOffset, duration: blockDuration};
  }
}

interface Duration {
  hours: number;
  minutes: number;
  milliseconds: number;
}
function getDurationBetweenInHoursAndMinutes(start: Date, end: Date): Duration {
  const milliseconds = end.valueOf() - start.valueOf();
  const minutes = Math.floor(milliseconds / 1000 / 60) % 60;
  const hours = Math.floor(milliseconds / 1000 / 60 / 60) + (minutes < 0 ? 1 : 0);
  return {hours, minutes, milliseconds};
}

/**
 * generate timeline start hour with start and end padding
 */
function getTimelineStartHour(start: Date, end: Date) {
  const duration = getDurationBetweenInHoursAndMinutes(
    floorDate(start, BlockMinutesInMilliseconds),
    ceilDate(end, BlockMinutesInMilliseconds)
  );

  const startHours = start.getHours();
  const endHours = startHours + duration.hours;

  const timelineHours = duration.hours;
  const reserveStartPadding = start.getMinutes() > 0 ? 1 : 0;
  // if there is space to adjust timeline
  if (timelineHours < TimelineDurationHours) {
    let startPadding = TimelineDurationHours - timelineHours - reserveStartPadding;
    startPadding = Math.min(startPadding, TimelinePaddingHours);

    let endPadding = 0;
    // if there is still space after including start padding
    if (startPadding + timelineHours < TimelineDurationHours) {
      endPadding = TimelineDurationHours - timelineHours - startPadding;
      endPadding = Math.min(endPadding, TimelinePaddingHours);
    }

    // adjust start hour to precede when it starts before the default start hour after including start padding
    if (startHours - startPadding <= TimelineDefaultStartHour) {
      return startHours - startPadding;
    }
    // adjust start hour to exceed when it starts later and duration exceeds to the default end hour after including end padding
    if (endHours + endPadding > TimelineDefaultStartHour + TimelineDurationHours) {
      return endHours + endPadding - TimelineDurationHours;
    }
    return TimelineDefaultStartHour;
  } else {
    return startHours;
  }
}

function createHoursRange(startHour: number, range: number) {
  return Array.from({length: range}, (_, hourBlock) => {
    const hour = (startHour + hourBlock) % 24;
    return to12HourFormat(hour);
  });
}

function to12HourFormat(hour: number) {
  const amPm = hour >= 12 ? 'pm' : 'am';
  hour = hour % 12;
  hour = hour === 0 ? 12 : hour;
  return hour + ' ' + amPm;
}

function floorDate(date: Date, ms: number) {
  if (ms <= 0) {
    throw new Error('"ms" should be greater than 0');
  }
  return new Date(Math.floor(date.valueOf() / ms) * ms);
}

function ceilDate(date: Date, ms: number) {
  if (ms <= 0) {
    throw new Error('"ms" should be greater than 0');
  }
  return new Date(Math.ceil(date.valueOf() / ms) * ms);
}

function roundHoursAndMinutesToBlockSteps(hour: number, minutes: number) {
  return hour + Math.round(minutes / BlockMinutes) / BlockSteps;
}



// WEBPACK FOOTER //
// ./src/app/booking-management/booking-jobs/timeline.ts